import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  getProVesselTypeLabel,
  getRiskLevelColor,
  isNonVesselTypeSpecificProReport,
  ProReportResponse,
  ProVesselTypes,
} from '../models/pro.model';

function LabelValueRow({
  label,
  value,
  sx = {},
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  sx?: object;
}) {
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, ...sx }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

LabelValueRow.defaultProps = {
  sx: {},
};

function ProReportInfoList() {
  return (
    <Typography variant="body2">
      Download the full PRO report for more detailed information on:
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>
          {`Claims risk ratings, based on NorthStandard's claims experience.`}
        </li>
        <li>Advice from our expert loss prevention team.</li>
        <li>Feedback from our global network of correspondents.</li>
        <li>
          Key contact information for club contacts and local correspondents.
        </li>
      </ul>
    </Typography>
  );
}

function ProReportTable({ report }: { report: ProReportResponse }) {
  return (
    <TableContainer sx={{ mb: 1, boxShadow: 0 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                padding: '0.25rem 0',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              Vessel type
            </TableCell>
            <TableCell
              sx={{
                padding: '0.25rem 0',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              Port claims risk*
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isNonVesselTypeSpecificProReport(report) &&
            Object.entries(report.risk_level)
              .filter(([vesselType]) => vesselType !== ProVesselTypes.UNKNOWN)
              .map(([vesselType, riskLevel]) => (
                <TableRow key={vesselType}>
                  <TableCell sx={{ padding: '0.25rem 0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {getProVesselTypeLabel(vesselType as ProVesselTypes)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '0.25rem 0',
                      color: getRiskLevelColor(riskLevel),
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {riskLevel}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
          {!isNonVesselTypeSpecificProReport(report) && (
            <TableRow>
              <TableCell sx={{ padding: '0.25rem 0' }}>
                {getProVesselTypeLabel(report.vessel_type as ProVesselTypes)}
              </TableCell>
              <TableCell
                sx={{
                  padding: '0.25rem 0',
                  color: getRiskLevelColor(report.risk_level),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {report.risk_level}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ProReportPreview({ report }: { report: ProReportResponse }) {
  if (!report) return null;

  return (
    <Box
      sx={{
        maxWidth: 600,
        margin: '0 auto',
        textAlign: 'left',
      }}
      data-testid="pro-report-preview"
    >
      <ProReportTable report={report} />
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
      >
        {`*Risk levels by vessel type are based on NorthStandard's claims experience and/or correspondents' advice as outlined below.`}
      </Typography>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <ProReportInfoList />
    </Box>
  );
}

export default ProReportPreview;
